// Хуки для работы с picker Form3

import { useState, useCallback, useEffect } from 'react';

// Хук для работы с picker справочников
export const usePicker = (
  handbooks,
  displayData,
  setLocalFormData,
  setFormData
) => {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerField, setPickerField] = useState('');
  const [pickerOptions, setPickerOptions] = useState([]);
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  const [activeItemId, setActiveItemId] = useState(null);
  const [activeTableType, setActiveTableType] = useState('podlesok');

  // Функция для открытия picker справочника
  const openPicker = useCallback((fieldName, handbookId, itemId = null, tableType = 'podlesok') => {
    let options = [];
    
    const handbook = handbooks.find(h => h.id === handbookId);
    
    if (handbook && handbook.references) {
      options = handbook.references.map(r => ({
        value: r.id.toString(),
        label: r.name,
        description: r.description || null
      }));
    }
    
    if (options && options.length > 0) {
      let currentValue = '';
      
      if (itemId) {
        // Для полей таблицы берем значение из конкретного элемента
        let item = null;
        if (tableType === 'podlesok') {
          item = displayData.podlesok.find(p => p.id === itemId);
        } else if (tableType === 'podrost') {
          item = displayData.podrost.find(p => p.id === itemId);
        }
        currentValue = item ? item[fieldName] || '' : '';
        setActiveItemId(itemId);
        setActiveTableType(tableType);
      } else {
        // Для обычных полей используем стандартную логику
        currentValue = displayData[fieldName] || '';
        setActiveItemId(null);
        setActiveTableType('podlesok');
      }
      
      const selectedIndex = options.findIndex(opt => opt.value === currentValue);
      
      setPickerField(fieldName);
      setPickerOptions(options);
      setPickerSearch('');
      setSelectedOptionIndex(selectedIndex);
      setShowPicker(true);
    }
  }, [handbooks, displayData]);

  // Функция для закрытия picker
  const closePicker = useCallback(() => {
    setShowPicker(false);
    setPickerField('');
    setPickerOptions([]);
    setPickerSearch('');
    setSelectedOptionIndex(-1);
    setActiveItemId(null);
    setActiveTableType('podlesok');
  }, []);

  // Функция для выбора значения из picker
  const selectPickerValue = useCallback((value) => {
    const selectedOption = pickerOptions.find(opt => opt.value === value);
    const valueToSave = selectedOption ? selectedOption.value : value;
    
    if (activeItemId) {
      // Для полей таблицы обновляем конкретный элемент
      let newData = { ...displayData };
      
      if (activeTableType === 'podlesok') {
        const updatedPodlesok = displayData.podlesok.map(item => 
          item.id === activeItemId 
            ? { ...item, [pickerField]: valueToSave }
            : item
        );
        newData = { ...displayData, podlesok: updatedPodlesok };
      } else if (activeTableType === 'podrost') {
        const updatedPodrost = displayData.podrost.map(item => 
          item.id === activeItemId 
            ? { ...item, [pickerField]: valueToSave }
            : item
        );
        newData = { ...displayData, podrost: updatedPodrost };
      }
      
      setLocalFormData(newData);
      if (setFormData) {
        setFormData(newData);
      }
    } else {
      // Для обычных полей используем стандартную логику
      const newData = { 
        ...displayData, 
        [pickerField]: valueToSave
      };
      
      setLocalFormData(newData);
      if (setFormData) {
        setFormData(newData);
      }
    }
    
    closePicker();
  }, [pickerField, pickerOptions, activeItemId, activeTableType, displayData, setLocalFormData, setFormData, closePicker]);

  // Функция для фильтрации опций по поиску
  const getFilteredOptions = useCallback(() => {
    let filtered = pickerOptions;
    
    if (pickerSearch.trim()) {
      const searchLower = pickerSearch.toLowerCase();
      filtered = pickerOptions.filter(option => 
        option.label.toLowerCase().includes(searchLower) ||
        (option.description && option.description.toLowerCase().includes(searchLower))
      );
    }
    
    return filtered;
  }, [pickerOptions, pickerSearch]);

  // Функция для получения индекса выбранного элемента в отфильтрованном списке
  const getSelectedIndexInFiltered = useCallback(() => {
    const filtered = getFilteredOptions();
    if (selectedOptionIndex >= 0 && selectedOptionIndex < pickerOptions.length) {
      const selectedOption = pickerOptions[selectedOptionIndex];
      return filtered.findIndex(opt => opt.value === selectedOption.value);
    }
    return -1;
  }, [getFilteredOptions, selectedOptionIndex, pickerOptions]);

  // Эффект для прокрутки к выбранному элементу в picker
  useEffect(() => {
    if (showPicker) {
      const timer = setTimeout(() => {
        const filteredIndex = getSelectedIndexInFiltered();
        if (filteredIndex >= 0) {
          const selectedElement = document.querySelector(`.picker-list li:nth-child(${filteredIndex + 1})`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showPicker, selectedOptionIndex, getFilteredOptions, getSelectedIndexInFiltered, pickerOptions, pickerSearch]);

  return {
    showPicker,
    pickerField,
    pickerOptions,
    pickerValue: displayData[pickerField] || '',
    pickerSearch,
    selectedOptionIndex,
    openPicker,
    closePicker,
    selectPickerValue,
    getFilteredOptions,
    getSelectedIndexInFiltered,
    setPickerSearch
  };
};

